import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import {
  BILL_AGES,
  BILL_AGE_LABEL,
  CASE_STATUS_KEYS,
  CASE_STATUS_TONE,
  ORIGIN_LABEL,
  STAGE_LABEL,
  UNDO_OFFER_MS,
  casePath,
  caseStatus,
  isReleased,
  matchesSearch,
  releaseRefusal,
  type BillAge,
  type CaseStatusKey,
  type JobOrder,
  caseFinished,
} from '@/domain';
import {
  useAccountsUnreview,
  useHoldOrder,
  useJobOrders,
  useReleaseCase,
  useReleaseHold,
  useTakeBackRelease,
  useUndoPayment,
} from '@/application/usecases/useOrders';
import { useSession } from '@/application/usecases/useSession';
import { useTableOrder } from '@/application/usecases/useTableOrder';
import { useTableLayout } from '@/application/usecases/useTableLayout';
import { PAGE_SIZES, useTablePage } from '@/application/usecases/useTablePage';
import { failureWords } from '@/shared/failure/failure';
import { strings } from '@/shared/strings';
import { formatDay } from '@/shared/format/instant';
import { Button } from '@/ui/primitives/Button';
import { toast } from '@/ui/primitives/toastStore';
import { CaseTable } from '@/ui/data/CaseTable';
import { FilterBar, type FilterDef } from '@/ui/data/FilterBar';
import { SortSelect } from '@/ui/data/SortSelect';
import { casePresets } from '@/ui/data/casePresets';
import { CASE_STATUS_LABEL } from '@/ui/data/shippingLabels';
import { StatusWithNote } from '@/ui/data/StatusDot';
import { orderColumns } from '@/ui/data/orderColumns';
import { useSelection } from '@/ui/data/useSelection';
import { useMoney } from '@/ui/money/moneyView';
import { CurrencyBar } from './CurrencyBar';
import { FinanceFigures, OwedByDoctorList } from './FinanceOverview';
import { MoneySelectionActions, type MoneyAction } from './MoneySelectionActions';
import { paymentSummaryPath } from './paymentPaths';
import finance from './Finance.module.css';
import { DeliveryDate } from '@/ui/data/DeliveryDate';
import type { Column } from '@/ui/data/ResponsiveTable';
import { SkeletonRows } from '@/ui/feedback/Loading';
import { Tabs } from '@/ui/primitives/Tabs';
import { HoldDialog } from '../orders/HoldDialog';
import { useConfirmAction } from '../shell/useConfirmAction';
import { ChangesSheet, type ChangesMode } from './ChangesSheet';
import { ReceiptSheet } from './ReceiptSheet';
import { ReviewCleanupNotice } from './ReviewCleanupNotice';
import screen from '../orders/OrdersScreen.module.css';
import styles from './PaymentsScreen.module.css';

/**
 * Payments. Section 5.2, rebuilt as a table.
 *
 * It was four groups of cards down one long page: awaiting payment, on hold,
 * paid, dispatched. The client asked for a search, an order, a page control and
 * a table, and the groups had to go for any of that to mean anything: a search
 * across four groups is four searches, and a page across them is a page of
 * whichever group the cut falls in.
 *
 * Nothing is lost with them. Every group was a status, and the status is a
 * column with a filter above it, which also answers a question the groups could
 * not: show me every case on hold AND overdue.
 *
 * Accounts act on a row from the row: Mark paid, and Hold or Release. A case
 * that is already paid or gone carries no button, as its card carried none.
 *
 * Three lists since the request of 5 October 2026, each its own page from the
 * server: To be paid, Changed (edited since it was priced, waiting for Accounts
 * to look again) and Paid cases. Mark paid opens the case's receipt, which
 * asks once more with the total; the toast after it carries Undo, and the case
 * moves to Paid cases.
 *
 * A fourth since the request of 5 October 2026 (third), section 3: Reviewed.
 * Review on a Changed row opens what changed, before and after, and that sheet
 * is where the case is marked as reviewed; the toast after it carries Undo for
 * ten seconds, and the case moves to Reviewed. There its row opens the same
 * changes to read, with Mark as not reviewed for a mistake found later. A row
 * keeps two short buttons on every list: a label never wraps, and three do not
 * fit beside each other on a phone.
 *
 * Section 2 of the same request: every write from this screen is asked about
 * first. Release asks through the shared confirmation; Hold's dialog is the
 * question itself; Review and Mark paid already asked. An Undo is never asked
 * about, and Mark paid's now stays up for ten seconds, like every other.
 */
/*
 * Two more since the request of 8 October 2026 (fourth): Part paid, the cases
 * with some of their bill received and not all of it, and By doctor, which is
 * not a list of cases but what each doctor owes. Over all of them stand the
 * figures of what the lab is owed, each of which opens the list it counts.
 */
/*
 * Two more since the lab's request of 17 October 2026: Released, the cases
 * Accounts let go to be shipped before they were paid and still owe (item 8),
 * and In ceramic, every case with a kit at Ceramic now, assigned or not
 * (item 14). On To be paid, Part paid, Released and In ceramic the cases can
 * be ticked and marked paid or released together, each asked about first and
 * each with Undo (items 9 and 10). The ages of what is owed open To be paid
 * narrowed to that age (item 1).
 */
type MoneyList = 'unpaid' | 'part' | 'changed' | 'paid' | 'reviewed' | 'released' | 'ceramic' | 'doctors';
const MONEY_LISTS: readonly MoneyList[] = [
  'unpaid',
  'part',
  'changed',
  'paid',
  'reviewed',
  'released',
  'ceramic',
  'doctors',
];

function listOf(value: string | null): MoneyList {
  return MONEY_LISTS.find((list) => list === value) ?? 'unpaid';
}

/** What a ticked case on each list can have done to it. The lists that read money and not work get no boxes. */
const LIST_ACTIONS: Record<MoneyList, readonly MoneyAction[]> = {
  unpaid: ['pay', 'release'],
  part: ['pay', 'release'],
  ceramic: ['pay', 'release'],
  released: ['pay', 'unrelease'],
  changed: [],
  paid: [],
  reviewed: [],
  doctors: [],
};

const rowKeyOf = (row: JobOrder) => row.jobOrderNo;

export function PaymentsScreen() {
  const navigate = useNavigate();
  const { data: session } = useSession();
  const hold = useHoldOrder();
  const release = useReleaseHold();
  const undo = useUndoPayment();
  const unreview = useAccountsUnreview();
  const releaseCase = useReleaseCase();
  const takeBack = useTakeBackRelease();
  const ask = useConfirmAction();
  const money = useMoney();
  const [holding, setHolding] = useState<string | null>(null);
  const [receiptOf, setReceiptOf] = useState<string | null>(null);
  // The case whose changes are open, and whether to review them or to read what a review covered.
  const [changesOf, setChangesOf] = useState<{ jobOrderNo: string; mode: ChangesMode } | null>(
    null,
  );

  // The list is in the address, so Back from a case record lands on the same one.
  const [params, setParams] = useSearchParams();
  const list = listOf(params.get('list'));
  // How long the cases have been owed, from the ages over the lists. The request of 17 October 2026, item 1.
  const age: BillAge | null = BILL_AGES.find((each) => each === params.get('age')) ?? null;
  function chooseList(next: MoneyList) {
    setParams(next === 'unpaid' ? {} : { list: next }, { replace: true });
  }
  function chooseAge(next: BillAge | null) {
    setParams(next === null ? {} : { age: next }, { replace: true });
  }

  const [query, setQuery] = useState('');
  const [status, setStatus] = useState<CaseStatusKey | 'all'>('all');

  const paging = useTablePage('payments');
  // The columns as this reader laid them out, kept on their account. The request of 7 October 2026, section 3.
  const layout = useTableLayout('payments');
  /*
   * A dispatched case is on this list, because accounts follow the money past
   * the door, and the money is not finished when the parcel leaves. The page
   * comes from the server, as everywhere else.
   */
  const { data, isPending, isError } = useJobOrders(
    useMemo(
      () => ({
        page: paging.page,
        pageSize: paging.pageSize,
        includeDispatched: true,
        // By doctor is not a list of cases: nothing of this page is drawn under it.
        money: list === 'doctors' ? ('unpaid' as const) : list,
        ...(age === null || list !== 'unpaid' ? {} : { age }),
      }),
      [paging.page, paging.pageSize, list, age],
    ),
  );
  // How many cases wait for Accounts to look again, for the tab's badge.
  const { data: changedCount } = useJobOrders(
    useMemo(
      () => ({
        page: 1,
        pageSize: 1,
        includeDispatched: true,
        money: 'changed' as const,
      }),
      [],
    ),
  );

  const presets = useMemo(
    () => casePresets<JobOrder>(['newestFirst', 'dueSoonest', 'urgentFirst', 'doctorAtoZ']),
    [],
  );
  const order = useTableOrder('payments', presets);

  const seesValue = session?.permissions.includes('pay') === true
    || session?.permissions.includes('admin') === true;
  const pays = session?.permissions.includes('pay') === true;

  // The cases ticked for one action on all of them, across pages: for a holder of pay, on the lists that take one.
  const actions = pays ? LIST_ACTIONS[list] : [];
  const selection = useSelection(rowKeyOf);
  const { clear } = selection;
  useEffect(() => {
    clear();
  }, [clear, list, age]);

  const visible = useMemo(
    () =>
      // Read inside the callback: `data?.items ?? []` outside it is a new array
      // on every render, which would make this memo do nothing at all.
      (data?.items ?? []).filter(
        (row) =>
          matchesSearch(query, [
            row.jobOrderNo,
            // The number on the doctor's paper, which an invoice quotes. The lab's request of 17 October 2026, item 7.
            row.serialNumber,
            row.dentistName,
            row.patientName,
            ORIGIN_LABEL[row.origin],
          ]) &&
          (status === 'all' || caseStatus(row) === status),
      ),
    [data, query, status],
  );

  const filtering = query.trim() !== '' || status !== 'all';

  // A filter or a new order makes a different list, and page three of the old
  // one is nothing in the new one.
  const { reset } = paging;
  useEffect(() => {
    reset();
  }, [reset, query, status, list, age, order.presetKey, order.column]);

  const filters: FilterDef[] = [
    {
      kind: 'search',
      label: strings.payments.search,
      placeholder: strings.payments.searchPlaceholder,
      value: query,
      onChange: setQuery,
    },
    {
      kind: 'select',
      label: strings.orders.status,
      value: status,
      options: [
        { value: 'all', label: strings.orders.allStatuses },
        ...CASE_STATUS_KEYS.map((key) => ({ value: key, label: CASE_STATUS_LABEL[key] })),
      ],
      onChange: (next) => setStatus(next as CaseStatusKey | 'all'),
    },
  ];

  /**
   * Marked paid from the receipt, after its question: say so, offer Undo for
   * about ten seconds, and the case is on Paid cases. Undo is one tap and is
   * not asked about. The request of 5 October 2026 (third), section 2.
   */
  function onPaid(jobOrderNo: string) {
    toast(`${jobOrderNo} ${strings.payments.markedPaid}`, {
      tone: 'success',
      durationMs: UNDO_OFFER_MS,
      action: {
        label: strings.undo.action,
        onClick: () => {
          // By the promise: the toast outlives this screen, and its answer must still be said.
          undo.mutateAsync(jobOrderNo).then(
            () => toast(`${jobOrderNo} ${strings.payments.paymentUndone}`),
            (error: Error) => toast(failureWords(error), { tone: 'error' }),
          );
        },
      },
    });
  }

  /**
   * Released to be shipped before it is paid, after its question, with Undo
   * for about ten seconds. The lab's request of 17 October 2026, items 8 and 10.
   */
  function onRelease(jobOrderNo: string) {
    ask(
      strings.confirm.releaseCase(jobOrderNo),
      () =>
        releaseCase.mutateAsync(jobOrderNo).then(() =>
          toast(strings.payments.released(jobOrderNo), {
            tone: 'success',
            durationMs: UNDO_OFFER_MS,
            action: {
              label: strings.undo.action,
              onClick: () => {
                takeBack.mutateAsync(jobOrderNo).then(
                  () => toast(strings.payments.releaseTakenBack(jobOrderNo)),
                  (error: Error) => toast(failureWords(error), { tone: 'error' }),
                );
              },
            },
          }),
        ),
      { confirmLabel: strings.payments.releaseToShip },
    );
  }

  /** A release taken back from Released, after its question. */
  function onTakeBack(jobOrderNo: string) {
    ask(
      strings.confirm.takeBackRelease(jobOrderNo),
      () =>
        takeBack.mutateAsync(jobOrderNo).then(() =>
          toast(strings.payments.releaseTakenBack(jobOrderNo), {
            tone: 'success',
            durationMs: UNDO_OFFER_MS,
            action: {
              label: strings.undo.action,
              onClick: () => {
                releaseCase.mutateAsync(jobOrderNo).then(
                  () => toast(strings.payments.released(jobOrderNo)),
                  (error: Error) => toast(failureWords(error), { tone: 'error' }),
                );
              },
            },
          }),
        ),
      { confirmLabel: strings.payments.takeBackRelease },
    );
  }

  /** The changes sheet goes, unless another case's has been opened in the meantime. */
  function closeChangesOf(jobOrderNo: string) {
    setChangesOf((open) => (open?.jobOrderNo === jobOrderNo ? null : open));
  }

  /**
   * Marked as reviewed from the changes sheet: say so and offer Undo for about
   * ten seconds, and the case is on Reviewed. Undo is the call Mark as not
   * reviewed makes, and is not asked about: it is the way back from a mistake.
   * The request of 5 October 2026 (third), section 3.
   */
  function onReviewed(jobOrderNo: string) {
    closeChangesOf(jobOrderNo);
    toast(strings.payments.markedReviewed(jobOrderNo), {
      tone: 'success',
      durationMs: UNDO_OFFER_MS,
      action: {
        label: strings.undo.action,
        onClick: () => {
          // By the promise: the toast outlives this screen, and its answer must still be said.
          unreview.mutateAsync(jobOrderNo).then(
            () => toast(strings.payments.reviewUndone(jobOrderNo)),
            (error: Error) => toast(failureWords(error), { tone: 'error' }),
          );
        },
      },
    });
  }

  /** Marked as not reviewed, after the question: the case is back on Changed. */
  function onNotReviewed(jobOrderNo: string) {
    closeChangesOf(jobOrderNo);
    toast(strings.payments.markedNotReviewed(jobOrderNo), { tone: 'success' });
  }

  const columns: Column<JobOrder>[] = [
    ...(seesValue
      ? ([
          {
            key: 'value',
            label: strings.payments.value,
            mono: true,
            align: 'right',
            nowrap: true,
            sortable: true,
            sortValue: (row) => row.value ?? 0,
            render: (row) => (row.value == null ? '' : money(row.value)),
          },
          {
            /*
             * What the case still owes, which is what Accounts chase, and the
             * way to its payment summary. A link, so the row's own tap still
             * opens the case. The request of 8 October 2026 (fourth).
             */
            key: 'balance',
            label: strings.finance.balance,
            align: 'right',
            nowrap: true,
            sortable: true,
            sortValue: (row) => row.payment.balance ?? 0,
            render: (row) => (
              <span className={finance.stack}>
                <Link
                  className={finance.link}
                  to={paymentSummaryPath(row.jobOrderNo)}
                  aria-label={`${strings.finance.summaryLabel} ${row.jobOrderNo}`}
                >
                  {money(row.payment.balance ?? row.value ?? 0)}
                </Link>
                {row.payment.status === 'part' && row.payment.paidAmount != null && row.payment.due != null && (
                  <span className={finance.under}>
                    {strings.finance.paidOfDue(money(row.payment.paidAmount), money(row.payment.due))}
                  </span>
                )}
              </span>
            ),
          },
        ] as Column<JobOrder>[])
      : []),
    ...(list === 'changed'
      ? ([
          {
            key: 'changes',
            label: strings.payments.whatChanged,
            render: (row) => (
              <span className={styles.changes}>
                {row.accountsReview?.changes ?? ''}
                {row.accountsReview && (
                  <span className={styles.changedOn}>
                    {strings.payments.changedOn} {formatDay(row.accountsReview.since)}
                  </span>
                )}
              </span>
            ),
          },
        ] as Column<JobOrder>[])
      : []),
    {
      key: 'deliveryDate',
      label: strings.orders.deliveryDate,
      nowrap: true,
      sortable: true,
      sortValue: (row) => row.deliveryDate,
      render: (row) => <DeliveryDate date={row.deliveryDate} finished={caseFinished(row)} />,
    },
    // Only a paid case has a day it was paid; on the other lists the column is empty.
    ...(list === 'paid'
      ? ([
          {
            key: 'paidAt',
            label: strings.payments.paidOn,
            mono: true,
            nowrap: true,
            sortable: true,
            sortValue: (row) => row.payment.paidAt ?? '',
            render: (row) => (row.payment.paidAt === null ? '' : formatDay(row.payment.paidAt)),
          },
        ] as Column<JobOrder>[])
      : []),
    // Who released the case, and the day. The lab's request of 17 October 2026, item 8.
    ...(list === 'released'
      ? ([
          {
            key: 'releasedAt',
            label: strings.payments.releasedColumn,
            sortable: true,
            sortValue: (row) => row.release?.at ?? '',
            render: (row) =>
              row.release == null ? (
                ''
              ) : (
                <span className={styles.changes}>
                  {row.release.byName ?? ''}
                  <span className={styles.changedOn}>{formatDay(row.release.at)}</span>
                </span>
              ),
          },
        ] as Column<JobOrder>[])
      : []),
    // Whose bench the case is on at Ceramic, or nobody's yet. Item 14 of the same request.
    ...(list === 'ceramic'
      ? ([
          {
            key: 'atCeramic',
            label: strings.payments.withWhom,
            render: (row) => (
              <span className={styles.changes}>
                {row.currentStage === null ? '' : STAGE_LABEL[row.currentStage]}
                <span className={styles.changedOn}>{row.holderName ?? strings.orders.nobodyYet}</span>
              </span>
            ),
          },
        ] as Column<JobOrder>[])
      : []),
    // Who marked the case as reviewed, and the day. Only the Reviewed list has an answer.
    ...(list === 'reviewed'
      ? ([
          {
            key: 'reviewedAt',
            label: strings.payments.reviewedColumn,
            sortable: true,
            sortValue: (row) => row.accountsReviewed?.at ?? '',
            render: (row) =>
              row.accountsReviewed ? (
                <span className={styles.changes}>
                  {row.accountsReviewed.byName}
                  <span className={styles.changedOn}>{formatDay(row.accountsReviewed.at)}</span>
                </span>
              ) : (
                ''
              ),
          },
        ] as Column<JobOrder>[])
      : []),
    {
      key: 'actions',
      label: strings.payments.actions,
      render: (row) => <RowActions row={row} onHold={() => setHolding(row.jobOrderNo)} />,
    },
    // What the rows carry and the table does not show as it comes. The request of 8 October 2026 (second).
    ...orderColumns(['createdAt', 'serialNumber', 'caseType', 'product', 'origin', 'currentStage', 'clinic', 'redoOf']),
  ];

  /** What accounts can do to one case, which is decided by where the case is. */
  function RowActions({ row, onHold }: { row: JobOrder; onHold: () => void }) {
    const receiptButton = (
      <Button onClick={() => setReceiptOf(row.jobOrderNo)}>{strings.payments.viewReceipt}</Button>
    );

    if (list === 'changed') {
      return (
        <div className={styles.actions}>
          {receiptButton}
          {/* What changed first, before and after; the sheet it opens marks the case as reviewed. */}
          <Button
            variant="primary"
            onClick={() => setChangesOf({ jobOrderNo: row.jobOrderNo, mode: 'waiting' })}
          >
            {strings.payments.review}
          </Button>
        </div>
      );
    }

    if (list === 'reviewed') {
      return (
        <div className={styles.actions}>
          {receiptButton}
          {/* To read again, and to mark as not reviewed from there. */}
          <Button onClick={() => setChangesOf({ jobOrderNo: row.jobOrderNo, mode: 'reviewed' })}>
            {strings.payments.whatChangedButton}
          </Button>
        </div>
      );
    }

    if (list === 'paid') return <div className={styles.actions}>{receiptButton}</div>;

    if (row.state === 'held') {
      return (
        <div className={styles.actions}>
          <Button
            disabled={release.isPending}
            onClick={() =>
              // Asked first; a refusal is said by the confirmation, in the server's words.
              ask(
                strings.confirm.releaseHold(row.jobOrderNo),
                () =>
                  release
                    .mutateAsync(row.jobOrderNo)
                    .then(() => toast(`${row.jobOrderNo} ${strings.payments.holdReleased}`)),
                { confirmLabel: strings.payments.releaseHold },
              )
            }
          >
            {/* Release hold, not Release: since 17 October 2026 Release is what lets a case ship unpaid. */}
            {strings.payments.releaseHold}
          </Button>
        </div>
      );
    }

    // Paid: the money is settled and there is nothing to press.
    if (row.payment.paid) return null;

    // Released, and still owed: the release can be taken back while the case is in the lab.
    if (isReleased(row)) {
      return (
        <div className={styles.actions}>
          {!row.dispatch.dispatched && (
            <Button disabled={takeBack.isPending} onClick={() => onTakeBack(row.jobOrderNo)}>
              {strings.payments.takeBack}
            </Button>
          )}
          <Button variant="primary" onClick={() => setReceiptOf(row.jobOrderNo)}>
            {strings.payments.markPaid}
          </Button>
        </div>
      );
    }

    return (
      <div className={styles.actions}>
        {!row.dispatch.dispatched && <Button onClick={onHold}>{strings.payments.hold}</Button>}
        {/* To be shipped before it is paid. The lab's request of 17 October 2026, item 8. */}
        {releaseRefusal(row) === null && (
          <Button disabled={releaseCase.isPending} onClick={() => onRelease(row.jobOrderNo)}>
            {strings.payments.releaseToShip}
          </Button>
        )}
        {/* The invoice first, then a question with the total, then the payment. */}
        <Button variant="primary" onClick={() => setReceiptOf(row.jobOrderNo)}>
          {strings.payments.markPaid}
        </Button>
      </div>
    );
  }

  return (
    <div>
      <div className={screen.head}>
        <span className={screen.lbl}>{strings.payments.label}</span>
        <h2>{strings.payments.title}</h2>
        <span className={screen.hint}>{strings.payments.hint}</span>
      </div>

      <CurrencyBar />

      <FinanceFigures current={list} age={age} onShow={chooseList} onShowAge={chooseAge} />

      <Tabs
        label={strings.payments.tabsLabel}
        current={list}
        onSelect={chooseList}
        tabs={[
          { key: 'unpaid', label: strings.payments.tabToPay },
          { key: 'part', label: strings.finance.tabPart },
          {
            key: 'changed',
            label: strings.payments.tabChanged,
            badge: changedCount?.total,
          },
          { key: 'paid', label: strings.payments.tabPaid },
          { key: 'reviewed', label: strings.payments.tabReviewed },
          { key: 'released', label: strings.payments.tabReleased },
          { key: 'ceramic', label: strings.payments.tabCeramic },
          { key: 'doctors', label: strings.finance.tabDoctors },
        ]}
      >
        {/* For a manager who holds this screen too; the same notice is on Orders. */}
        {list === 'changed' && <ReviewCleanupNotice />}

        {/* What each doctor owes: not a list of cases, so none of the bar and the table under it. */}
        {list === 'doctors' && <OwedByDoctorList />}

        {list !== 'doctors' && (
          <>
        <FilterBar
          label={strings.payments.filtersLabel}
          filters={filters}
          sort={<SortSelect presets={presets} value={order.presetKey} onChange={order.choose} />}
        />

        {/* To be paid narrowed to one age of what is owed, and the way back to every age. */}
        {list === 'unpaid' && age !== null && (
          <p className={styles.ageNote}>
            {strings.payments.agedOnly(BILL_AGE_LABEL[age])}{' '}
            <Button variant="text" onClick={() => chooseAge(null)}>
              {strings.payments.everyAge}
            </Button>
          </p>
        )}

        {isPending && <SkeletonRows rows={6} label={strings.common.loadingOrders} />}
        {isError && <p className={screen.hint}>{strings.errors.server}</p>}

        {!isPending && !isError && (
          <CaseTable
            customize={layout}
            columns={columns}
            rows={order.apply(visible)}
            selection={actions.length > 0 ? selection : undefined}
            sort={order.column}
            onSortChange={order.sortByColumn}
            identity={(row) => ({
              jobOrderNo: row.jobOrderNo,
              urgent: row.urgent,
              // The mark of a remake. The request of 6 October 2026 (second), item 2.
              redo: row.redoOf !== null,
              held: row.hold.held,
              dentistName: row.dentistName,
              patientName: row.patientName,
            })}
            status={(row) => {
              const key = caseStatus(row);
              return (
                <StatusWithNote
                  tone={CASE_STATUS_TONE[key]}
                  note={
                    row.hold.held
                      ? row.hold.reason
                      : !row.payment.paid && isReleased(row)
                        ? strings.payments.releasedNote
                        : null
                  }
                >
                  {CASE_STATUS_LABEL[key]}
                </StatusWithNote>
              );
            }}
            statusSortValue={(row) => CASE_STATUS_KEYS.indexOf(caseStatus(row))}
            rowKey={(row) => row.jobOrderNo}
            onRowClick={(row) => {
              // Accounts hold the archive's record screen, not Orders': the door
              // that exists for them. Section 13 of the review of 2 October 2026.
              // Back from the record comes back to this list, not to the archive.
              const path = casePath(session?.views ?? [], { jobOrderNo: row.jobOrderNo }, 'record');
              if (path !== null)
                void navigate(path, {
                  state: {
                    backTo: {
                      path: list === 'unpaid' ? '/payments' : `/payments?list=${list}`,
                      label: strings.payments.title,
                    },
                  },
                });
            }}
            caption={strings.payments.caption}
            emptyHeading={
              filtering
                ? strings.table.noMatch
                : list === 'paid'
                  ? strings.payments.paidEmptyHeading
                  : list === 'part'
                    ? strings.finance.partEmptyHeading
                  : list === 'changed'
                    ? strings.payments.changedEmptyHeading
                    : list === 'reviewed'
                      ? strings.payments.reviewedEmptyHeading
                      : list === 'released'
                        ? strings.payments.releasedEmptyHeading
                        : list === 'ceramic'
                          ? strings.payments.ceramicEmptyHeading
                          : strings.payments.emptyHeading
            }
            emptyDetail={
              filtering
                ? strings.table.noMatchDetail
                : list === 'paid'
                  ? strings.payments.paidEmptyDetail
                  : list === 'part'
                    ? strings.finance.partEmptyDetail
                  : list === 'changed'
                    ? strings.payments.changedEmptyDetail
                    : list === 'reviewed'
                      ? strings.payments.reviewedEmptyDetail
                      : list === 'released'
                        ? strings.payments.releasedEmptyDetail
                        : list === 'ceramic'
                          ? strings.payments.ceramicEmptyDetail
                          : strings.payments.emptyDetail
            }
            paging={{
              page: paging.page,
              pageSize: paging.pageSize,
              total: data?.total ?? 0,
              sizes: PAGE_SIZES,
              onPage: paging.goTo,
              onPageSize: paging.choose,
              label: strings.table.pagesLabel,
            }}
          />
        )}

        {/* After the table, so the bar appearing moves no row under the pointer. */}
        {actions.length > 0 && (
          <MoneySelectionActions
            rows={selection.rows}
            actions={actions}
            onClear={selection.clear}
            onSelectPage={
              visible.every((row) => selection.selected.has(row.jobOrderNo))
                ? undefined
                : () => selection.onTogglePage(visible, true)
            }
          />
        )}
          </>
        )}
      </Tabs>

      {receiptOf !== null && (
        <ReceiptSheet jobOrderNo={receiptOf} onClose={() => setReceiptOf(null)} onPaid={onPaid} />
      )}

      {changesOf !== null && (
        <ChangesSheet
          // Another case is another sheet: a refusal said about the last one does not carry over.
          key={`${changesOf.mode}-${changesOf.jobOrderNo}`}
          jobOrderNo={changesOf.jobOrderNo}
          mode={changesOf.mode}
          onClose={() => setChangesOf(null)}
          onReviewed={onReviewed}
          onNotReviewed={onNotReviewed}
        />
      )}

      {holding !== null && (
        <HoldDialog
          jobOrderNo={holding}
          busy={hold.isPending}
          onCancel={() => setHolding(null)}
          onConfirm={(reason) =>
            hold.mutate(
              { jobOrderNo: holding, reason },
              {
                onSuccess: () => {
                  toast(`${holding} ${strings.payments.putOnHold}`);
                  setHolding(null);
                },
                onError: (error: Error) => toast(failureWords(error)),
              },
            )
          }
        />
      )}
    </div>
  );
}
